import { useMutation, type UseMutationOptions } from '@tanstack/react-query';

import { i18n } from '~/utils';

import {
  type DismissParsingCaseMutationResponse,
  type DismissParsingCaseVariables,
} from '../types';
import { useParsingCaseActionMutationOptions } from '../useParsingCaseActionMutationOptions';

import { dismissParsingCase } from './dismissParsingCase';

/**
 * Custom hook to dismiss a parsing case, setting its status to DISMISSED.
 *
 * @param options - Optional react-query mutation options
 * @returns The mutation result
 *
 * @see https://app.dev.vestigas.com/redoc#operation/post_dismiss_parsing_case_delivery_note_parsing_cases__case_id__actions_dismiss_post
 */
export const useMutationDismissParsingCase = (
  options?: UseMutationOptions<
    DismissParsingCaseMutationResponse,
    unknown,
    DismissParsingCaseVariables
  >,
) => {
  const actionOptions =
    useParsingCaseActionMutationOptions<DismissParsingCaseVariables>(() =>
      i18n.t(($) => $.dismissParsingCase.success, { ns: 'data' }),
    );

  return useMutation<
    DismissParsingCaseMutationResponse,
    unknown,
    DismissParsingCaseVariables
  >({
    mutationFn: async (variables) => dismissParsingCase(variables),
    ...actionOptions,
    ...options,
  });
};
